import { useCallback, useEffect, useRef, useState } from 'react'
import type { PendingOrderInfo } from '../types/api'
import {
  getNotificationPermission,
  getPendingNotifyEnabled,
  isBrowserNotificationSupported,
  requestNotificationPermission,
  setPendingNotifyEnabled,
  showBrowserNotification,
} from '../utils/browserNotification'

type PermissionState = NotificationPermission | 'unsupported'

const EXECUTED_EVENT_TYPES = new Set(['open', 'close', 'partial_close'])

type TradeEventRow = {
  id: number
  event_type: string
  symbol?: string | null
  side?: string | null
  contracts?: number | string | null
  entry_price?: number | string | null
  exit_price?: number | string | null
  realized_pnl?: number | string | null
  status?: string | null
}

function formatPendingBody(order: PendingOrderInfo): string {
  const side = order.side === 'buy' ? '买' : '卖'
  const intent = order.intent === 'open' ? '开仓' : '平仓'
  const qty = order.effective_contracts ?? order.original_contracts
  return `${order.symbol} ${intent}${side} · 数量 ${qty}`
}

function formatTradeEventBody(event: TradeEventRow): string {
  const symbol = event.symbol || '-'
  const qty = event.contracts != null ? String(event.contracts) : '-'
  const side = event.side || ''
  const price = event.exit_price ?? event.entry_price
  const priceText = price != null ? ` · 价格 ${price}` : ''
  const pnl = event.realized_pnl
  const pnlText = pnl != null && pnl !== '' ? ` · 盈亏 ${Number(pnl) >= 0 ? '+' : ''}${Number(pnl).toFixed(2)}U` : ''

  if (event.event_type === 'open') {
    return `${symbol} 开仓 ${side} · 数量 ${qty}${priceText}`
  }
  if (event.event_type === 'partial_close') {
    return `${symbol} 部分平仓 ${side} · 数量 ${qty}${pnlText}${priceText}`
  }
  return `${symbol} 平仓 ${side} · 数量 ${qty}${pnlText}${priceText}`
}

function executedEventTitle(event: TradeEventRow): string {
  if (event.event_type === 'open') return '订单已开仓'
  if (event.event_type === 'partial_close') return '订单已部分平仓'
  return '订单已平仓'
}

type Options = {
  tokenId: number
  tokenName?: string
  pendingOrders: PendingOrderInfo[] | undefined
  tradeEvents: TradeEventRow[] | undefined
  enabled: boolean
  onOpenPendingTab?: () => void
  onOpenPositionsTab?: () => void
}

export function usePanelOrderNotifications({
  tokenId,
  tokenName,
  pendingOrders,
  tradeEvents,
  enabled,
  onOpenPendingTab,
  onOpenPositionsTab,
}: Options) {
  const supported = isBrowserNotificationSupported()
  const [notifyEnabled, setNotifyEnabled] = useState(() => getPendingNotifyEnabled(tokenId))
  const [permission, setPermission] = useState<PermissionState>(() => getNotificationPermission())

  const seenPendingIdsRef = useRef<Set<number> | null>(null)
  const seenTradeEventIdsRef = useRef<Set<number> | null>(null)
  const onOpenPendingTabRef = useRef(onOpenPendingTab)
  const onOpenPositionsTabRef = useRef(onOpenPositionsTab)
  onOpenPendingTabRef.current = onOpenPendingTab
  onOpenPositionsTabRef.current = onOpenPositionsTab

  useEffect(() => {
    seenPendingIdsRef.current = null
    seenTradeEventIdsRef.current = null
    setNotifyEnabled(getPendingNotifyEnabled(tokenId))
  }, [tokenId])

  const seedPendingIds = useCallback((orders: PendingOrderInfo[]) => {
    seenPendingIdsRef.current = new Set(
      orders.filter((o) => o.status === 'pending').map((o) => o.id),
    )
  }, [])

  const seedTradeEventIds = useCallback((events: TradeEventRow[]) => {
    seenTradeEventIdsRef.current = new Set(events.map((e) => e.id))
  }, [])

  const seedAll = useCallback((orders?: PendingOrderInfo[], events?: TradeEventRow[]) => {
    if (orders) seedPendingIds(orders)
    if (events) seedTradeEventIds(events)
  }, [seedPendingIds, seedTradeEventIds])

  useEffect(() => {
    if (!enabled || !notifyEnabled || permission !== 'granted' || !pendingOrders) return

    const pending = pendingOrders.filter((o) => o.status === 'pending')

    if (seenPendingIdsRef.current === null) {
      seedPendingIds(pendingOrders)
      return
    }

    const seen = seenPendingIdsRef.current
    const newcomers = pending.filter((o) => !seen.has(o.id))
    if (newcomers.length === 0) return

    const account = tokenName ? `【${tokenName}】` : ''
    if (newcomers.length === 1) {
      const o = newcomers[0]
      showBrowserNotification({
        title: `${account}待执行订单待放行`,
        body: formatPendingBody(o),
        tag: `pending-order-${tokenId}-${o.id}`,
        onClick: () => onOpenPendingTabRef.current?.(),
      })
    } else {
      showBrowserNotification({
        title: `${account}有 ${newcomers.length} 笔待执行订单`,
        body: newcomers.map(formatPendingBody).join('\n'),
        tag: `pending-order-batch-${tokenId}`,
        onClick: () => onOpenPendingTabRef.current?.(),
      })
    }

    newcomers.forEach((o) => seen.add(o.id))
  }, [enabled, notifyEnabled, permission, pendingOrders, tokenId, tokenName, seedPendingIds])

  useEffect(() => {
    if (!enabled || !notifyEnabled || permission !== 'granted' || !tradeEvents) return

    if (seenTradeEventIdsRef.current === null) {
      seedTradeEventIds(tradeEvents)
      return
    }

    const seen = seenTradeEventIdsRef.current
    const newcomers = tradeEvents.filter(
      (e) => EXECUTED_EVENT_TYPES.has(e.event_type) && e.status !== 'failed' && !seen.has(e.id),
    )
    if (newcomers.length === 0) return

    const account = tokenName ? `【${tokenName}】` : ''
    if (newcomers.length === 1) {
      const e = newcomers[0]
      showBrowserNotification({
        title: `${account}${executedEventTitle(e)}`,
        body: formatTradeEventBody(e),
        tag: `trade-event-${tokenId}-${e.id}`,
        onClick: () => onOpenPositionsTabRef.current?.(),
      })
    } else {
      showBrowserNotification({
        title: `${account}有 ${newcomers.length} 笔订单已成交`,
        body: newcomers.map(formatTradeEventBody).join('\n'),
        tag: `trade-event-batch-${tokenId}`,
        onClick: () => onOpenPositionsTabRef.current?.(),
      })
    }

    newcomers.forEach((e) => seen.add(e.id))
  }, [enabled, notifyEnabled, permission, tradeEvents, tokenId, tokenName, seedTradeEventIds])

  const toggleNotify = useCallback(async (next: boolean) => {
    if (!supported) return false

    if (next) {
      const perm = await requestNotificationPermission()
      setPermission(perm)
      if (perm !== 'granted') {
        setPendingNotifyEnabled(tokenId, false)
        setNotifyEnabled(false)
        return false
      }
      setPendingNotifyEnabled(tokenId, true)
      setNotifyEnabled(true)
      seedAll(pendingOrders, tradeEvents)
      return true
    }

    setPendingNotifyEnabled(tokenId, false)
    setNotifyEnabled(false)
    return true
  }, [supported, tokenId, pendingOrders, tradeEvents, seedAll])

  return {
    supported,
    notifyEnabled: notifyEnabled && permission === 'granted',
    permission,
    toggleNotify,
    shouldPollInBackground: notifyEnabled && permission === 'granted',
  }
}

/** @deprecated use usePanelOrderNotifications */
export const usePendingOrderNotifications = usePanelOrderNotifications
